import { getExample } from 'awesome-phonenumber';
import { getCountryCodeByName } from './input-phone-helpers';

// Type definition for expected phone length
type PhoneLength = {
  min: number;
  max: number;
};

// Function to get expected phone number length from placeholder (internal use only)
const getExpectedPhoneLengthFromPlaceholder = (
  countryCode: string,
): PhoneLength => {
  if (!countryCode) {
    return { min: 7, max: 15 }; // Fallback range
  }

  let countryAbbr = null;
  if (countryCode.length === 2) {
    countryAbbr = countryCode.toUpperCase();
  } else {
    countryAbbr = getCountryCodeByName(countryCode);
  }

  // Special handling for China - mobile (11 digits) and landline (10-11 digits) numbers
  if (countryAbbr === 'CN') {
    return { min: 10, max: 11 };
  }

  if (countryAbbr) {
    try {
      const example = getExample(countryAbbr);
      if (example && example.number && example.number.international) {
        // CRITICAL FIX: Calculate length the same way as placeholder
        // Remove dialing code FIRST, then count digits (matches placeholder logic)
        const nationalFormat = example.number.international.replace(
          /^\+\d+\s*/,
          '',
        );
        const digitCount = nationalFormat.replace(/\D/g, '').length;

        // Allow for some flexibility in certain countries that can have variable length numbers
        const flexibleCountries = ['gb', 'de', 'it', 'jp', 'br']; // Countries that can have variable length
        const isFlexible = flexibleCountries.includes(
          countryCode.toLowerCase(),
        );

        if (isFlexible) {
          return { min: Math.max(digitCount - 1, 7), max: digitCount + 1 };
        }
        return { min: digitCount, max: digitCount };
      }
    } catch (error) {
      // Silently fall back to default values if phone example lookup fails
    }
  }

  // Fallback for unknown countries
  return { min: 7, max: 15 };
};

// Helper function for country-specific format validation (internal use only)
const validateCountrySpecificFormat = (
  digits: string,
  countryCode: string,
): boolean => {
  if (!countryCode || !digits) {
    return true;
  }

  const country = countryCode.toLowerCase();

  switch (country) {
    case 'us':
    case 'ca':
      // US/Canada: Area code and exchange cannot start with 0 or 1
      if (digits.length === 10) {
        const areaCode = digits.substring(0, 3);
        const exchange = digits.substring(3, 6);
        return (
          areaCode[0] >= '2' &&
          areaCode[0] <= '9' &&
          exchange[0] >= '2' &&
          exchange[0] <= '9'
        );
      }
      return true; // Allow other lengths for flexibility

    case 'in':
      // Indian mobile numbers start with 6-9
      if (digits.length >= 1) {
        return digits[0] >= '6' && digits[0] <= '9';
      }
      return true;

    case 'cn':
      // Chinese phone numbers: mobile and landline formats
      if (digits.length >= 2) {
        // Mobile numbers: 1[3-9]XXXXXXXXX (11 digits)
        const isMobile =
          digits[0] === '1' && digits[1] >= '3' && digits[1] <= '9';

        // Beijing landline: 10XXXXXXXX (11 digits)
        const isBeijingLandline =
          digits.startsWith('10') && digits.length === 11;

        // Major city landlines: 2[0-9]XXXXXXXX (10 digits)
        const isMajorCityLandline = digits[0] === '2' && digits.length === 10;

        return isMobile || isBeijingLandline || isMajorCityLandline;
      }
      return true;

    case 'fr':
      // French mobile numbers start with 6 or 7, landlines have geographic codes
      if (digits.length >= 1) {
        const firstDigit = digits[0];
        return firstDigit >= '1' && firstDigit <= '9'; // Basic validation
      }
      return true;

    case 'gb':
      // UK numbers can start with various codes
      if (digits.length >= 2) {
        // Mobile: 7XXX, some landlines: 1XXX, 2XXX, etc.
        const prefix = digits.substring(0, 2);
        return /^[1-9]\d$/.test(prefix); // First digit 1-9, second digit 0-9
      }
      return true;

    default:
      return true; // Unknown country, allow
  }
};

// Function to get maximum allowed input length (including formatting)
export const getMaxAllowedLength = (
  inputValue: string,
  currentCountry: { value: string; code?: string } | null,
): number => {
  if (!currentCountry) {
    return 25;
  } // Default fallback

  const isInternational = inputValue.startsWith('+');
  const expectedLength = getExpectedPhoneLengthFromPlaceholder(
    currentCountry.value,
  );

  if (isInternational) {
    // For international format: country code + space + expected digits + formatting
    const countryCodeLength = (currentCountry.code || '+1').length;
    const spacesAndFormatting = Math.ceil(expectedLength.max / 3) + 2; // Rough estimate for formatting
    return countryCodeLength + expectedLength.max + spacesAndFormatting;
  }
  // For local format: expected digits + formatting
  const spacesAndFormatting = Math.ceil(expectedLength.max / 3) + 1; // Rough estimate for formatting
  return expectedLength.max + spacesAndFormatting;
};

// Main phone validation function
export const validatePhoneNumber = (
  phoneValue: string,
  phoneNumberIsRequired: string,
  invalidPhone: string,
  countryCode: string | null = null,
  dialingCode: string | null = null,
): string => {
  const phoneNum = phoneValue?.trim();
  if (!phoneNum) {
    return phoneNumberIsRequired;
  }

  // Basic length validation (4-15 digits) - fallback for unknown countries
  const digitCount = (phoneNum.match(/\d/g) || []).length;
  if (digitCount < 4 || digitCount > 15) {
    return invalidPhone;
  }

  // Check for multiple plus signs or plus signs not at the beginning
  const plusCount = (phoneNum.match(/\+/g) || []).length;
  if (plusCount > 1 || (plusCount === 1 && !phoneNum.startsWith('+'))) {
    return invalidPhone;
  }

  // Check for consecutive special characters (like multiple plus signs, dashes, etc.)
  if (/[+\-\s()]{3,}/.test(phoneNum)) {
    return invalidPhone;
  }

  // Extract only digits for validation
  const digits = phoneNum.replace(/\D/g, '');

  // Check if it's an international format
  const isInternational = phoneNum.startsWith('+');

  // Use placeholder-based validation for length checking
  if (countryCode) {
    // Get expected length from placeholder (this is already without dialing code)
    const expectedLength = getExpectedPhoneLengthFromPlaceholder(countryCode);

    // Always determine the national number digits (without dialing code)
    let nationalDigits = digits;

    if (dialingCode) {
      const expectedDialingCode = (dialingCode as string).substring(1); // Remove the +

      // For international format, verify the dialing code matches
      if (isInternational) {
        if (!digits.startsWith(expectedDialingCode)) {
          return invalidPhone;
        }
      }

      // Remove dialing code from digits for validation (both international and local)
      // This handles cases like "+1 555-123-4567" and "1555123456"
      if (digits.startsWith(expectedDialingCode)) {
        nationalDigits = digits.substring(expectedDialingCode.length);
      }
    }

    // Validate the national number length (always without dialing code)
    if (
      nationalDigits.length < expectedLength.min ||
      nationalDigits.length > expectedLength.max
    ) {
      return invalidPhone;
    }

    // Apply country-specific format validation on national digits only
    if (!validateCountrySpecificFormat(nationalDigits, countryCode)) {
      return invalidPhone;
    }
  }

  return '';
};
